import { useEffect, useState } from "react";
import { Box, Typography, IconButton, Drawer, Button, Checkbox } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useTheme } from "@mui/material/styles";
import { useToastContext } from "../../context/toast";
import LockIcon from "@mui/icons-material/Lock";

import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import {
	arrayMove,
	SortableContext,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { COL_MAX_MSG } from "../../constants/ShipmentConstants";

export default function ConfigColumn({ columns, handleClose, handleApplyColumns }) {
	const theme = useTheme();
	const { showErrorToast } = useToastContext();
	const [columnsList, setColumnsList] = useState(columns);
	const [selectedCols, setSelectedCols] = useState([]);
	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

	const handleClosePopup = () => {
		handleClose(false);
	};

	const onSave = () => {
		handleApplyColumns(columnsList);
	};

	const handleDragEnd = (event) => {
		const { active, over } = event;
		if (active.id !== over.id) {
			const oldIndex = columnsList.findIndex((item) => item.id === active.id);
			const newIndex = columnsList.findIndex((item) => item.id === over.id);
			setColumnsList((prev) => arrayMove(prev, oldIndex, newIndex));
		}
	};

	const handleSelectionChange = (col) => {
		if (col.isDefault) {
			return;
		}

		const isAlreadySelected = col.isSelected;

		if (selectedCols.length < 10 || (selectedCols.length === 10 && isAlreadySelected)) {
			setColumnsList((prev) =>
				prev.map((c) => (c.id === col.id ? { ...c, isSelected: !isAlreadySelected } : c)),
			);
		} else if (selectedCols.length >= 10 && !isAlreadySelected) {
			showErrorToast(COL_MAX_MSG);
		}
	};

	useEffect(() => {
		setSelectedCols(
			columnsList.length ? columnsList.filter((d) => d.isSelected).map((k) => k.id) : [],
		);
	}, [columnsList]);

	return (
		<Drawer
			anchor="right"
			open={true}
			onClose={handleClosePopup}
			PaperProps={{
				sx: {
					boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
					borderTopLeftRadius: "8px",
					borderBottomLeftRadius: "8px",
				},
			}}
		>
			<Box
				sx={{
					width: 377,
					height: "100%",
					overflow: "auto",
				}}
			>
				{/* header */}
				<Box>
					<Typography
						sx={{
							fontFamily: "Poppins",
							fontSize: "16px",
							fontWeight: 700,
							lineHeight: "24px",
							letterSpacing: "0.5px",
							textAlign: "left",
							margin: "15px",
							gap: 10,
							height: 24,
							color: theme.palette.text.primary,
						}}
						variant="h6"
						gutterBottom
					>
						Config Table
					</Typography>
					<Typography
						sx={{
							fontFamily: "Poppins",
							fontSize: "12px",
							fontWeight: 500,
							lineHeight: "16px",
							letterSpacing: "0.5px",
							textAlign: "left",
							margin: "15px",
							height: 32,
							gap: 10,
							color: theme.palette.text.primary,
						}}
						variant="h6"
						gutterBottom
					>
						Selected label will be visible in the column, All other labels will be under the
						expandable section
					</Typography>
				</Box>

				{/* list of options */}
				{columnsList && (
					<Box
						style={{
							width: "100%",
						}}
					>
						<Box sx={{ width: "100%" }}>
							<DndContext
								sensors={sensors}
								collisionDetection={closestCenter}
								onDragEnd={handleDragEnd}
							>
								<SortableContext
									items={columnsList.map((i) => i.id)}
									strategy={verticalListSortingStrategy}
								>
									{columnsList.map((col) => (
										<SortableItem
											key={col.id}
											item={col}
											isSelected={selectedCols.includes(col.id)}
											onToggle={() => handleSelectionChange(col)}
											theme={theme}
										/>
									))}
								</SortableContext>
							</DndContext>
						</Box>
					</Box>
				)}
			</Box>

			{/* bottom section */}
			<Box
				sx={{
					display: "flex",
					flexDirection: "row",
					justifyContent: "space-between",
					alignItems: "center",
					padding: "20px",
					marginTop: "auto",
				}}
			>
				<Button
					variant="text"
					sx={{
						textTransform: "none",
						color: theme.palette.text.primary,
						fontFamily: "Poppins, sans-serif",
					}}
					onClick={handleClosePopup}
				>
					Cancel
				</Button>
				<Button
					variant="contained"
					color="primary"
					sx={{
						textTransform: "none",
						borderRadius: "80px",
						bgcolor: theme.palette.button.main,
						"&:hover": { bgcolor: theme.palette.buttonhover.main },
						fontFamily: "Poppins, sans-serif",
					}}
					onClick={onSave}
				>
					Apply
				</Button>
			</Box>
		</Drawer>
	);
}

function SortableItem({ item, isSelected, onToggle, theme }) {
	const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: item.id });
	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	};

	return (
		<Box
			ref={setNodeRef}
			key={item.id}
			style={style}
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				borderBottom: "1px solid #E8F2FF",
				paddingRight: 2,
				paddingLeft: 0.5,
			}}
		>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					height: 42,
				}}
			>
				{/* Drag icon */}
				<IconButton {...attributes} {...listeners} sx={{ cursor: "grab" }}>
					<DragIndicatorIcon />
				</IconButton>
				<Checkbox
					style={{
						cursor: item.isDefault ? "not-allowed !important" : "pointer",
						width: 26,
						height: 26,
						marginRight: 8,
						marginLeft: 8,
					}}
					sx={{
						color: theme.palette.text.button,
						"&.Mui-checked": { color: theme.palette.text.button },
					}}
					checked={isSelected}
					onChange={() => onToggle(item.id, item.isDefault)}
				/>
				<Typography
					style={{
						fontFamily: "Poppins",
						fontWeight: 400,
						fontSize: 16,
						letterSpacing: "0.5px",
					}}
				>
					{item.label}
				</Typography>
			</Box>
			{item.isDefault && <LockIcon style={{ fill: theme.palette.text.button }} />}
		</Box>
	);
}
